import {
    CommunicationMethod,
    CustomAction, DelegatePages, DirectionId,
    FeatureSettings,
    InitMessage, MessageData,
    MessageId, OpenPageSettings, Pause, POPErrorTypes,
    SoundSettings,
    SupportedFeature, UCIPMessage, VisibilityChange
} from '@gongxigames/framework/integrations/pop-ucip/types';
import {
    consoleLogger,
    CoreDI,
    IBrowser,
    IEventManagerWithAsync,
    LanguageService,
    lazyInject,
    ListenerTypeWithID
} from '@gongxigames/framework/core';
import {POPUcipGameClientWrapper} from '@gongxigames/framework/integrations/pop-ucip/POPUcipGameClientWrapper';
import {
    AutoplayEvents, BigWinEvents,
    BottomPanelEvents, BuyBonusButtonEvents, BuyBonusPopUpEvents, FlowModuleEvents, GameRulesEvents,
    GlobalEventsMapForBackwardCompatibility, InfoPageEvents, KeypadControlsEvents, LogicEvents, Message, MessageText, WinMeterEvents, WinPresentationEvents
} from '@gongxigames/framework/slot';
import {RubyPlayGameClientWrapperSettings} from '@gongxigames/framework/integrations/rubyplay/RubyPlayGameClientWrapperSettings';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';

// here the list of features that can be supported by our games
// we do not support / implement these features: 'switchgame', 'gamechange', 'switchmode'

const CLIENT_FEATURES: SupportedFeature[] = [
    'closegame', 'sound', 'openpage', 'loadingstatus', 'errors', 'gameround',
    'autoplay', 'betupdate', 'balanceupdate', 'balancerefresh', 'pause'
];

type BlockingReason = 'round' | 'ui' | 'feature';

export class UCIPInterface {
    public static readonly NAME: symbol = Symbol('UCIPInterface');
    public static readonly HANDSHAKE_TIMEOUT: number = 1000;

    @lazyInject(CoreDI.LanguageService)
    protected readonly languageService!: LanguageService;

    protected _clientWrapper: POPUcipGameClientWrapper;
    protected _eventManager: IEventManagerWithAsync<GlobalEventsMapForBackwardCompatibility>;
    protected _browser: IBrowser;
    protected _attempts = 60;
    protected _isCompletedHandshake = false;
    protected _origin = '*';
    protected _supportedFeatureIds: Map<SupportedFeature, FeatureSettings> = new Map([['basic', null]]);
    protected _needToPause = false;
    protected _blockingPauseReasons = new Set<BlockingReason>();
    protected _balance?: number;
    protected _roundEnded = false;

    constructor(clientWrapper: POPUcipGameClientWrapper, eventManager: IEventManagerWithAsync, browser: IBrowser) {
        this._clientWrapper = clientWrapper;
        this._eventManager = eventManager;
        this._browser = browser;

        this.setupMessageListener();

        this.sendInitializationRequest();
    }

    public completeHandshake(callback: Function): void {
        if (this._isCompletedHandshake) {
            callback();
        } else {
            this._browser.addTimeout(() => this.waitingForHandshakeComplete(callback), UCIPInterface.HANDSHAKE_TIMEOUT);
        }
    }

    protected waitingForHandshakeComplete(callback: Function): void {
        if (this._attempts > 0) {
            this.completeHandshake(callback);
            this._attempts--;
        } else {
            this.sendCloseGameFrameCommand();
        }
    }

    protected setupMessageListener(): void {
        this._browser.addEventListener('message', this.onReceivedMessageCallback.bind(this), false);
    }

    protected onReceivedMessageCallback(event: any): void {
        const {origin, data} = event;

        try {
            const parsedData = JSON.parse(data);
            const {_type} = parsedData;
            const featureId = this.getFeatureIdFromType(_type);

            if (!featureId || !this._supportedFeatureIds.has(featureId)) {
                consoleLogger.warn('Error occurred or such feature is not supported', data);

                return;
            }

            const messageId = this.getResponseMessageId(_type) as MessageId;

            switch (messageId) {
                case 'Initialization': {
                    this.onInitialization(parsedData, origin);
                    break;
                }
                case 'CloseGame': {
                    this.onCloseGame();
                    break;
                }
                case 'SoundUpdate': {
                    this.onSoundUpdate(parsedData);
                    break;
                }
                case 'VisibilityChange': {
                    this.onVisibilityChange(parsedData);
                    break;
                }
                case 'InterruptGameplay': {
                    this.onInterruptGameplay();
                    break;
                }
                case 'RefreshBalance': {
                    this.onRefreshBalance();
                    break;
                }
                case 'Pause': {
                    this.onPause(parsedData);
                    break;
                }
                case 'CustomAction': {
                    this.onCustomAction(parsedData);
                    break;
                }
                default: {
                    consoleLogger.warn('Message is not recognized', parsedData);
                    break;
                }
            }
        } catch (error) {
            consoleLogger.error(error);
        }
    }

    // message callbacks below
    protected onInitialization(data: InitMessage, origin: string): void {
        this._origin = origin;
        this._isCompletedHandshake = true;

        this.setSupportedFeatures(data.features);
        this.setupSoundSettings();
        this.setupGameEvents();
    }

    protected onCloseGame(): void {
        if (this._clientWrapper.isNextActionSpin()) {
            // when all needed session termination actions had been taken we have to send response:
            // TODO: here we can do something before operator forces exiting from the game
            this.postMessage({_type: 'ucip.closegame.g2wCloseGameResponse'});
        } else {
            // if we can not terminate the game we should send error:
            this.postMessage({_type: 'ucip.closegame.g2wCloseGameErrorResponse'});
        }
    }

    protected onSoundUpdate(data: SoundSettings): void {
        const {level, mute} = data;

        this.muteSound(mute);
        this.isSoundLevelValid(level) && this.updateSoundVolume(level / 100);
    }

    protected updateSoundVolume(volume: number): void {
        this._clientWrapper.getSettings<RubyPlayGameClientWrapperSettings>().set('soundVolume', volume);

        this._eventManager.emit(BottomPanelEvents.CHANGE_SOUND_LEVEL, volume);
    }

    protected muteSound(muted: boolean): void {
        this._clientWrapper.getSettings<RubyPlayGameClientWrapperSettings>().set('soundsMuted', muted);

        strictUiEmitter.emit(muted ? 'UI.sounds.disable' : 'UI.sounds.enable');
    }

    protected onVisibilityChange(data: VisibilityChange): void {
        const {visible} = data;

        if (visible) {
            this.enableGameUI();
        } else {
            this.disableGameUI();
        }

        this.muteSound(!visible);
    }

    protected onPause(data: Pause): void {
        const {pause} = data;

        this._needToPause = pause;

        if (pause) {
            this.shouldBePaused && this.pauseGame();
        } else {
            this.unpauseGame();
        }
    }

    protected onInterruptGameplay(): void {
        this._eventManager.emit(AutoplayEvents.FORCE_STOP_AUTOPLAY);
    }

    protected onRefreshBalance(): void {
        this._clientWrapper.refreshBalanceRequest();
    }

    protected onCustomAction(_data: CustomAction): void {
        // TODO: here could be added any custom action
    }
    // on message callbacks above

    protected setupGameEvents(): void {
        if (this.isFeatureSupported('sound')) {
            strictUiEmitter.on('UI.sounds.switchedTo', this.sendSoundUpdateNotification.bind(this), {owner: this});
        }

        if (this.isFeatureSupported('loadingstatus')) {
            this.on('General.ResourcesLoading.PROGRESS', this.sendLoadingProgressNotification);
            this.on('General.Game.INITIALIZED', this.sendLoadingEndNotification);
        }

        if (this.isFeatureSupported('gameround')) {
            this.on(LogicEvents.ROUND_START, this.sendGameRoundStartNotification);
            this.on(FlowModuleEvents.ROUND_ENDED, this.sendGameRoundEndNotification);
        }

        if (this.isFeatureSupported('autoplay')) {
            this.on(AutoplayEvents.START_AUTOPLAY, this.sendAutoplayStartNotification);
            this.on(AutoplayEvents.STOP_AUTOPLAY, this.sendAutoplayEndNotification);
            this.on('Autoplay.STARTED', this.sendAutoplayStartNotification);
            this.on('Autoplay.STOPPED', this.sendAutoplayEndNotification);
        }

        if (this.isFeatureSupported('betupdate')) {
            this.on(KeypadControlsEvents.TOTAL_BET_CHANGED, this.sendBetUpdateNotification);
        }

        if (this.isFeatureSupported('balanceupdate')) {
            this.on('General.UI.BALANCE.UPDATED', this.onBalanceUpdate);
            this.on(LogicEvents.BALANCE_UPDATED, this.onLogicBalanceUpdated);
        }

        if (this.isFeatureSupported('pause')) {
            this.on(LogicEvents.ROUND_STARTED, this.addBlockingProcessReason.bind(this, 'round'));
            this.on(FlowModuleEvents.NO_WIN, this.removeBlockingProcessReason.bind(this, 'round'));
            this.on(WinPresentationEvents.INITIAL_ANIMATION_SKIPPED, this.removeBlockingProcessReason.bind(this, 'round'));
            this.on('General.WinPresentation.WIN_METER_FINISHED', this.removeBlockingProcessReason.bind(this, 'round'));
            this.on(WinMeterEvents.SNAP_TO_FINAL_VALUE, this.removeBlockingProcessReason.bind(this, 'round'));
            this.on(WinMeterEvents.STOP_AFTER_SHOWING_FINAL_VALUE, this.removeBlockingProcessReason.bind(this, 'round'));
            this.on(BigWinEvents.FINISHED, this.removeBlockingProcessReason.bind(this, 'round'));

            this.on(FlowModuleEvents.FEATURE_STARTED, this.addBlockingProcessReason.bind(this, 'feature'));
            this.on(FlowModuleEvents.FEATURE_FINISHED, this.onFeatureFinished);

            this.on(BuyBonusButtonEvents.BUY_BONUS_BUTTON_CLICKED, this.addBlockingProcessReason.bind(this, 'feature'));
            this.on(BuyBonusPopUpEvents.BUY_POP_UP_CLOSED, this.removeBlockingProcessReason.bind(this, 'feature'));

            strictUiEmitter.on('UI.menu.gameRulesClicked', this.addBlockingProcessReason.bind(this, 'ui'));
            this.on(BottomPanelEvents.GAME_RULES_CLICKED, this.addBlockingProcessReason.bind(this, 'ui'));
            this.on(GameRulesEvents.CLOSED, this.removeBlockingProcessReason.bind(this, 'ui'));

            this.on(KeypadControlsEvents.INFO_CLICKED, this.addBlockingProcessReason.bind(this, 'ui'));
            strictUiEmitter.on('UI.menu.infoPageClicked', this.addBlockingProcessReason.bind(this, 'ui'));
            this.on(InfoPageEvents.CLOSED, this.removeBlockingProcessReason.bind(this, 'ui'));

            strictUiEmitter.on('UI.menu.historyButtonClicked', this.addBlockingProcessReason.bind(this, 'ui'));
            this.on(BottomPanelEvents.HISTORY_BUTTON_CLICKED, this.addBlockingProcessReason.bind(this, 'ui'));
            this.on('General.GameHistory.Closed', this.removeBlockingProcessReason.bind(this, 'ui'));

            this.on('General.Popups.Add', this.addBlockingProcessReason.bind(this, 'ui'));
            this.on('General.Popups.Remove', this.removeBlockingProcessReason.bind(this, 'ui'));
        }

        if (this.isFeatureSupported('errors')) {
            this.on('General.Message.SHOW', this.sendErrorNotification);
            this.on('General.ResourcesLoading.ERROR', this.sendLoadErrorNotification);
        }
    }

    // send methods below
    protected sendInitializationRequest(): void {
        this.postMessage({_type: 'ucip.basic.g2wInitializationRequest', version: '1.0.0', features: CLIENT_FEATURES});
    }

    public sendCloseGameFrameCommand(): void {
        this.postMessage({_type: 'ucip.basic.g2wCloseGameFrameCommand'});
    }

    protected sendSoundUpdateNotification(isOn: boolean): void {
        this.postMessage({
            _type: 'ucip.sound.g2wSoundUpdateNotification',
            mute: !isOn,
            level: this.floatToInteger(this._clientWrapper.getSettings<RubyPlayGameClientWrapperSettings>().get('soundVolume'))
        });
    }

    public sendOpenPageCommand(pageID: DelegatePages, url = ''): void {
        this.postMessage({_type: 'ucip.openpage.g2wOpenPageCommand', pageID, url});
    }

    protected sendLoadingProgressNotification(id: string | symbol, progress: number): void {
        progress = this.floatToInteger(progress);

        if (progress === 0) {
            this.postMessage({_type: 'ucip.loadingstatus.g2wLoadingStartNotification'});
        } else {
            this.postMessage({_type: 'ucip.loadingstatus.g2wLoadingProgressNotification', progress});
        }
    }

    protected sendLoadingEndNotification(): void {
        this.postMessage({_type: 'ucip.loadingstatus.g2wLoadingEndNotification'});
        this.postMessage({_type: 'ucip.loadingstatus.g2wGameReadyNotification'});
    }

    protected sendGameRoundStartNotification(id: string | symbol, bet: number): void {
        // send it only for paid rounds (bet > 0)
        if (bet) {
            this._roundEnded = false;
            this.postMessage({_type: 'ucip.gameround.g2wGameRoundStartNotification'});
        }
    }

    protected sendGameRoundEndNotification(): void {
        // sent it only in the end of paid round (meaning that next action is also should be paid)
        if (this._clientWrapper.isNextActionSpin() && !this._roundEnded) {
            this._roundEnded = true;
            this.postMessage({_type: 'ucip.gameround.g2wGameRoundEndNotification'});
        }
    }

    protected sendAutoplayStartNotification(): void {
        this.postMessage({_type: 'ucip.autoplay.g2wAutoplayStartNotification'});
    }

    protected sendAutoplayEndNotification(): void {
        this.postMessage({_type: 'ucip.autoplay.g2wAutoplayEndNotification'});
    }

    protected sendBetUpdateNotification(id: string | symbol, currentBetIndex: number): void {
        const totalBet = this._clientWrapper.getCurrentBet(currentBetIndex);

        this.postMessage({_type: 'ucip.betupdate.g2wBetUpdateNotification', totalBet});
    }

    protected onBalanceUpdate(id: string | symbol, balance: number): void {
        this._balance = balance;

        if (this._balance !== balance) {
            const win = Math.max(0, balance - this._balance);

            this._balance = balance;

            const balanceObj = this._clientWrapper.getBalanceData(balance, win);

            this.postMessage({_type: 'ucip.balanceupdate.g2wBalanceUpdateNotification', ...balanceObj});
        }
    }

    protected onLogicBalanceUpdated(id: string | symbol, balance: number): void {
        this._balance = balance;

        this.postMessage({
            _type: 'ucip.balanceupdate.g2wBalanceUpdateNotification',
            ...this._clientWrapper.getBalanceData(balance)
        });
    }

    protected sendOpenSuiteCommand(): void {
        // TODO: finish this when will implement native
        const suiteId = '';
        const subpage = '';

        this.postMessage({_type: 'ucip.native.opensuite.g2wOpenSuiteCommand', suiteId, subpage});
    }

    protected sendLoadErrorNotification(): void {
        this.postMessage({_type: 'ucip.errors.g2wErrorNotification', error: {
            type: 'GENERIC_ERROR',
            description: this.languageService.get('connectionError')
        }});
    }

    protected sendErrorNotification(eventId: string, error: Message): void {
        const {id, message} = error;
        const type = this.getErrorType(id);
        const description = this.getErrorMessageText(message);

        this.postMessage({_type: 'ucip.errors.g2wErrorNotification', error: {type, description}});
    }

    protected sendPauseNotification(pause: boolean): void {
        this.postMessage({_type: 'ucip.pause.g2wPauseNotification', pause});
    }

    // send methods above

    protected getErrorMessageText(message?: MessageText): string {
        if (message) {
            return typeof message === 'string' ? message : this.languageService.get(message.id);
        }

        return '';
    }

    protected getErrorType(errorId: string): POPErrorTypes {
        switch (errorId) {
            case 'SLOW_CONNECTION':
            case 'LOST_CONNECTION':
            case 'CONNECTION_ERROR':
            case 'GAME_UNAVAILABLE':
            case 'CONNECTION_TIMEOUT': {
                return 'CONNECTION_ERROR';
            }
            case 'INSUFFICIENT_FUNDS':
            case 'LOW_BALANCE_ERROR': {
                return 'INSUFFICIENT_FUNDS';
            }
            case 'SESSION_EXPIRED': {
                return 'AUTHENTICATION_ERROR';
            }
            case 'NOT_CRITICAL_CUSTOM_MESSAGE':
            case 'CRITICAL_CUSTOM_MESSAGE':
            case 'CUSTOM_ERROR':
            case 'CUSTOM_SERVER_ERROR':
            case 'BROKEN_ROUND':
            case 'BET_PLACE':
            case 'PLAYER_BLOCKED':
            case 'INTERNAL_ERROR': {
                return 'GENERIC_ERROR';
            }
            default: {
                return 'GENERIC_ERROR';
            }
        }
    }

    protected isSoundLevelValid(level: number): boolean {
        return typeof level === 'number' && level >= 0 && level <= 100;
    }

    protected setSupportedFeatures(features: (SupportedFeature | [SupportedFeature, FeatureSettings])[]): void {
        features?.forEach((feature: SupportedFeature | [SupportedFeature, FeatureSettings]) => {
            if (Array.isArray(feature)) {
                this._supportedFeatureIds.set(feature[0], feature[1]);
            } else {
                this._supportedFeatureIds.set(feature, null);
            }
        });
    }

    protected setupSoundSettings(): void {
        const settings = this._supportedFeatureIds.get('sound');

        settings && this.onSoundUpdate(settings as SoundSettings);
    }

    protected disableGameUI(): void {
        this._eventManager.emit('General.UI.DISABLE', UCIPInterface.NAME);
        this._eventManager.emit('Autoplay.StopButton.DISABLE', UCIPInterface.NAME);
    }

    protected enableGameUI(): void {
        this._eventManager.emit('General.UI.ENABLE', UCIPInterface.NAME);
        this._eventManager.emit('Autoplay.StopButton.ENABLE', UCIPInterface.NAME);
    }

    protected pauseGame(): void {
        this.disableGameUI();
        this._eventManager.emitAsync('General.System.PAUSE');
        this.sendPauseNotification(true);
    }

    protected unpauseGame(): void {
        this.enableGameUI();
        this._eventManager.emitAsync('General.System.RESUME');
        this.sendPauseNotification(false);
    }

    protected addBlockingProcessReason(reason: BlockingReason): void {
        this._blockingPauseReasons.add(reason);
    }

    protected removeBlockingProcessReason(reason: BlockingReason): void {
        this._blockingPauseReasons.delete(reason);

        this.shouldBePaused && this.pauseGame();
    }

    protected onFeatureFinished(): void {
        this._blockingPauseReasons.clear();

        this.shouldBePaused && this.pauseGame();
    }

    protected getFeatureIdFromType(type: UCIPMessage): SupportedFeature {
        return type?.split('.')[1] as SupportedFeature;
    }

    protected getResponseMessageId(type: UCIPMessage): MessageId {
        const methods: CommunicationMethod[] = ['Request', 'Response', 'Command', 'Notification'];
        const method = methods.filter(method => type.includes(method)).join('');
        const directions: DirectionId[] = ['w2g', 'g2w'];
        const direction = directions.filter(direction => type.includes(direction)).join('');

        return type.split('.')[2].slice(direction.length, -method.length) as MessageId;
    }

    protected on<K extends keyof GlobalEventsMapForBackwardCompatibility>(
        id: K,
        callback: ListenerTypeWithID<K, GlobalEventsMapForBackwardCompatibility[K]>,
        priority?: number
    ): void {
        this._eventManager.on(id, callback.bind(this, id) as any, this, priority);
    }

    protected postMessage(message: MessageData): void {
        this._browser.postMessageToParent(JSON.stringify(message), this._origin);
    }

    protected floatToInteger(digit: number): number {
        return Math.round(100 * digit);
    }

    public get shouldBePaused(): boolean {
        return this._needToPause && !this._blockingPauseReasons.size;
    }

    public isDelegateOpening(featureId: DelegatePages): boolean {
        if (this.isFeatureSupported('openpage')) {
            const openpageSettings = this.getFeatureSettings('openpage') as OpenPageSettings;

            return !openpageSettings || openpageSettings.delegatePages.includes(featureId);
        }

        return false;
    }

    public isFeatureSupported(featureId: SupportedFeature): boolean {
        return this._supportedFeatureIds.has(featureId);
    }

    public getFeatureSettings(featureId: SupportedFeature): FeatureSettings | undefined {
        return this._supportedFeatureIds.get(featureId);
    }
}
